"use client";

import { useMemo } from "react";
import { MatchList } from "@/components/matches";
import { mapMatchToMatchListItem } from "@/utilities/mappers/match";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useTeamLogoUrl } from "@/components/ui/TeamLogo";
import type { FUSportsMatchWithNativeId } from "@/utilities/data/types";

interface MatchesSectionFullProps {
    matches: FUSportsMatchWithNativeId[];
}

/**
 * Full match list for a tier. Reuses the site's `MatchList` component — same
 * component that renders team fixtures / results. Split into two ordered lists
 * (past + upcoming) with a section header between them.
 */
export function MatchesSectionFull({ matches }: MatchesSectionFullProps) {
    const labels = useLabels();
    const nav = useNavigation();
    const resolveLogoUrl = useTeamLogoUrl();

    const { finished, upcoming } = useMemo(() => {
        const finishedSorted = matches
            .filter((m) => m.status?.type === "FINISHED")
            .sort((a, b) => new Date(b.timing.kickoffTime).getTime() - new Date(a.timing.kickoffTime).getTime());
        const upcomingSorted = matches
            .filter((m) => {
                const t = m.status?.type;
                return t === "LIVE" || t === "NOT_STARTED" || t === undefined;
            })
            .sort((a, b) => new Date(a.timing.kickoffTime).getTime() - new Date(b.timing.kickoffTime).getTime());
        return { finished: finishedSorted, upcoming: upcomingSorted };
    }, [matches]);

    return (
        <div className="max-w-350 mx-auto px-4 py-6 space-y-10">
            <section>
                <h2 className="text-sm font-black uppercase tracking-widest mb-4">
                    {labels.homePage.upcomingMatches}
                </h2>
                <MatchList
                    matches={upcoming.map((m) =>
                        mapMatchToMatchListItem(m, false, false, nav.match, resolveLogoUrl),
                    )}
                    variant="teamFixtures"
                    emptyMessage={labels.homePage.noUpcomingMatches}
                />
            </section>
            <section>
                <h2 className="text-sm font-black uppercase tracking-widest mb-4">
                    {labels.homePage.latestResults}
                </h2>
                <MatchList
                    matches={finished.map((m) =>
                        mapMatchToMatchListItem(m, true, false, nav.match, resolveLogoUrl),
                    )}
                    variant="teamResults"
                    emptyMessage={labels.homePage.noRecentResults}
                />
            </section>
        </div>
    );
}
